import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  GameState,
  Position,
  PieceType,
  IMove,
  IPiece,
  MoveType,
} from "..\/..\/..\/..\/types";
import { MCTSUtils_v2 } from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 16: Piece-Specific Heuristics
 *
 * Handles advanced tactical patterns unique to specific pieces:
 * - 🐸 Frog patience (lily pad preservation for defense)
 * - 🐍 Snake risk assessment and positioning
 * - 🦏 Rhino self-sacrifice evaluation
 * - 🐵 Monkey combo opportunities
 * - 🍄 Fungi juke moves and positioning
 * - 🐧 Penguin pin setups
 * - 🦠 Virus bomb timing
 */
export class PieceSpecificHeuristicsPriority extends BaseDecisionPriority {
  readonly priority = 25;
  readonly name = "Piece-Specific Heuristics";
  readonly description = "Advanced tactical patterns for specific piece types";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Check if we have any pieces that benefit from specific heuristics
    const hasSpecial = this.hasSpecialPieces(context.gameState);
    const ourColor = context.gameState.isLightTurn ? "Light" : "Dark";
    const frogPosition = this.findUnusedFrog(context.gameState);

    // Always log relevance check for debugging
    if (frogPosition) {
      // Note: this.addLog() won't work here because analysisLogs isn't initialized yet
      // But this will appear in the analytics if the priority is relevant
    }

    return hasSpecial;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const ourColor = gameState.isLightTurn ? "Light" : "Dark";

    this.addLog(
      `🐸 Priority 16 ANALYZE called for ${ourColor} with ${context.allPossibleMoves.length} moves`
    );

    // 🐸 FROG PATIENCE: Check if frog should preserve lilypad for defense
    const frogResult = this.analyzeFrogPatience(
      gameState,
      context.allPossibleMoves
    );
    if (frogResult.shouldEarlyExit) {
      this.addLog(`🐸 Priority 16 - EARLY EXIT: ${frogResult.reasoning}`);
      return frogResult;
    }

    // Check if frog patience filtered any moves
    if (
      frogResult.filteredMoves &&
      frogResult.filteredMoves.length < context.allPossibleMoves.length
    ) {
      this.addLog(
        `🐸 Priority 16 - FILTERED: ${context.allPossibleMoves.length} -> ${frogResult.filteredMoves.length} moves`
      );
      return frogResult;
    }

    // Future piece-specific logic can be added here:
    // - Snake risk assessment
    // - Rhino sacrifice evaluation
    // - Monkey combo detection
    // - Fungi juke opportunities
    // - Penguin pin setups
    // - Virus bomb timing

    this.addLog(`🐸 Priority 16 - NO ACTION: ${frogResult.reasoning}`);
    return this.createNoActionResult("No piece-specific heuristics triggered");
  }

  /**
   * 🐸 FROG PATIENCE: Analyzes frog defensive strategy
   * Migrated from MCTSFrogDefense.ts
   */
  /**
   * 🐸 FROG PATIENCE: AGGRESSIVE BLOCKING STRATEGY
   *
   * PHILOSOPHY: Block ALL lilypad moves by default!
   * Higher priorities (1-15) can still use the frog for critical situations.
   * Lower priorities (17-19) will NEVER waste the frog on positional moves.
   */
  private analyzeFrogPatience(
    gameState: GameState,
    allPossibleMoves: MoveInfo[]
  ): DecisionResult {
    const frogPosition = this.findUnusedFrog(gameState);

    if (!frogPosition) {
      return {
        shouldEarlyExit: false,
        reasoning: "No unused frog found",
        priority: this.priority,
      };
    }

    this.addLog(
      `🐸 Found unused frog at [${frogPosition.row},${frogPosition.col}]`
    );

    // AGGRESSIVE STRATEGY: Remove ALL frog lilypad moves from consideration
    // Only higher priorities (1-15) should be able to use the frog
    const nonFrogMoves = this.removeAllFrogLilypadMoves(
      allPossibleMoves,
      frogPosition
    );

    if (nonFrogMoves.length < allPossibleMoves.length) {
      const blockedMoves = allPossibleMoves.length - nonFrogMoves.length;

      this.addLog(
        `🐸 FROG PATIENCE ENFORCED: Blocked ALL ${blockedMoves} lilypad moves`
      );
      this.addLog(
        `🐸 Only higher priorities (1-15) can use frog for critical situations`
      );
      this.addLog(
        `🐸 Lower priorities (17-19) cannot waste frog on positional moves`
      );

      return this.createFilterResult(
        nonFrogMoves,
        `FROG PATIENCE: Blocked all lilypad moves - only allow for critical priorities`,
        {
          logs: this.analysisLogs,
          frogPosition: frogPosition,
          blockedLilypadMoves: blockedMoves,
          strategy: "AGGRESSIVE_BLOCKING",
        }
      );
    }

    return {
      shouldEarlyExit: false,
      reasoning: "No frog lilypad moves to block",
      priority: this.priority,
    };
  }

  private hasSpecialPieces(gameState: GameState): boolean {
    const ourColor = gameState.isLightTurn;

    // Check for pieces that have special heuristics
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === ourColor) {
            // Currently only frog has implemented heuristics
            if (piece.type === PieceType.Frog && !piece.hasLilyJumped) {
              return true;
            }
            // Future: Add other piece types here
          }
        }
      }
    }
    return false;
  }

  private findUnusedFrog(gameState: GameState): Position | null {
    const ourColor = gameState.isLightTurn;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        const frog = square.Pieces.find(
          (p) =>
            p.type === PieceType.Frog &&
            p.isLight === ourColor &&
            !p.hasLilyJumped
        );

        if (frog) {
          return { row, col };
        }
      }
    }
    return null;
  }

  // NOTE: Old evaluateFrogDefensiveValue method removed
  // Now using aggressive blocking strategy - no need for complex analysis

  private findOurKing(
    gameState: GameState
  ): { piece: IPiece; position: Position } | null {
    const ourColor = gameState.isLightTurn;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === ourColor && piece.isKing) {
            return { piece, position: { row, col } };
          }
        }
      }
    }
    return null;
  }

  private findOpponentKing(
    gameState: GameState
  ): { piece: IPiece; position: Position } | null {
    const opponentColor = !gameState.isLightTurn;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === opponentColor && piece.isKing) {
            return { piece, position: { row, col } };
          }
        }
      }
    }
    return null;
  }

  private calculateDistanceToVictory(
    position: Position,
    isLight: boolean,
    boardHeight: number
  ): number {
    return isLight ? boardHeight - 1 - position.row : position.row;
  }

  private frogProvidesDefensiveCoverage(
    gameState: GameState,
    frogPosition: Position,
    opponentKingPosition: Position
  ): boolean {
    // Simple heuristic: frog provides defense if it's between opponent king and victory
    const opponentIsLight = !gameState.isLightTurn;
    const opponentVictoryRow = opponentIsLight ? gameState.board.length - 1 : 0;

    // Check if frog is positioned to potentially block opponent's path
    if (opponentIsLight) {
      // Opponent needs to move up, frog provides coverage if it's above opponent king
      return frogPosition.row > opponentKingPosition.row;
    } else {
      // Opponent needs to move down, frog provides coverage if it's below opponent king
      return frogPosition.row < opponentKingPosition.row;
    }
  }

  private findFrogCaptureOpportunities(
    gameState: GameState,
    frogPosition: Position
  ): MoveInfo[] {
    // Check for frog lilypad moves that result in captures
    const captureOpportunities: MoveInfo[] = [];

    // Frog lilypad can jump to any empty square or square with enemy pieces
    // We need to check all possible lily jump destinations for captures
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        if (row === frogPosition.row && col === frogPosition.col) continue;

        const targetSquare = gameState.board[row][col];
        const ourColor = gameState.isLightTurn;

        // Check if there are enemy pieces that can be captured
        const enemyPieces = targetSquare.Pieces.filter(
          (p) => p.isLight !== ourColor
        );
        if (enemyPieces.length > 0) {
          // This is a potential capture opportunity
          captureOpportunities.push({
            fromPosition: frogPosition,
            move: {
              id: `frog-capture-${row}-${col}` as any,
              pieceId: `frog-${frogPosition.row}-${frogPosition.col}` as any,
              position: { row, col },
              toPiece: enemyPieces[0], // Target piece being captured
              moveType: MoveType.capture,
              sideEffects: [],
            },
          });
        }
      }
    }

    return captureOpportunities;
  }

  // NOTE: Old filterFrogMovesForPreservation method removed
  // Now using aggressive blocking strategy

  private frogCanBlockRacePath(
    gameState: GameState,
    frogPosition: Position,
    opponentKingPosition: Position
  ): boolean {
    // Check if frog can use lilypad to block opponent king's direct path to victory
    const opponentIsLight = !gameState.isLightTurn;
    const opponentVictoryRow = opponentIsLight ? gameState.board.length - 1 : 0;

    // Calculate the direct path squares the opponent king might use
    const pathSquares: Position[] = [];

    // Simple path analysis - squares between opponent king and victory row
    const startRow = opponentKingPosition.row;
    const targetRow = opponentVictoryRow;
    const step = opponentIsLight ? 1 : -1;

    // Add squares in the direct path (same column as king, plus adjacent columns)
    for (let row = startRow + step; row !== targetRow + step; row += step) {
      // Center path (same column)
      pathSquares.push({ row, col: opponentKingPosition.col });

      // Adjacent paths (one column left/right)
      if (opponentKingPosition.col > 0) {
        pathSquares.push({ row, col: opponentKingPosition.col - 1 });
      }
      if (opponentKingPosition.col < gameState.board[0].length - 1) {
        pathSquares.push({ row, col: opponentKingPosition.col + 1 });
      }
    }

    // Check if frog can reach any of these blocking positions
    for (const pathSquare of pathSquares) {
      // Frog can lilypad to any square except where it currently is
      if (
        pathSquare.row !== frogPosition.row ||
        pathSquare.col !== frogPosition.col
      ) {
        // Check if square is empty or has only enemy pieces (frog can land there)
        const targetSquare = gameState.board[pathSquare.row][pathSquare.col];
        const ourColor = gameState.isLightTurn;
        const hasOurPieces = targetSquare.Pieces.some(
          (p) => p.isLight === ourColor
        );

        if (!hasOurPieces) {
          return true; // Frog can block this path square
        }
      }
    }

    return false;
  }

  private ourKingInImmediateDanger(
    gameState: GameState,
    ourKingPosition: Position
  ): boolean {
    // Check if our king can be captured on the opponent's next turn
    const ourColor = gameState.isLightTurn;

    // Simple threat detection - check if any enemy piece can reach our king
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        const enemyPieces = square.Pieces.filter((p) => p.isLight !== ourColor);

        for (const enemyPiece of enemyPieces) {
          // Simplified threat check - if enemy piece is adjacent or has long-range capabilities
          const distance =
            Math.abs(row - ourKingPosition.row) +
            Math.abs(col - ourKingPosition.col);

          // Different pieces have different threat ranges
          let threatRange = 1; // Default adjacent threat

          switch (enemyPiece.type) {
            case PieceType.Bear:
            case PieceType.Wolf:
              threatRange = 2; // Can move multiple squares
              break;
            case PieceType.Rhino:
              threatRange = 7; // Long linear range
              break;
            case PieceType.Crane:
            case PieceType.Penguin:
              threatRange = 3; // Fast movers
              break;
          }

          if (distance <= threatRange) {
            return true; // King is under threat
          }
        }
      }
    }

    return false;
  }

  /**
   * 🐸 AGGRESSIVE BLOCKING: Remove ALL frog lilypad moves
   * This ensures lower priorities cannot waste the frog on positional moves
   */
  private removeAllFrogLilypadMoves(
    allMoves: MoveInfo[],
    frogPosition: Position
  ): MoveInfo[] {
    let frogMovesCount = 0;

    const filteredMoves = allMoves.filter((moveInfo) => {
      // Keep all non-frog moves
      if (
        moveInfo.fromPosition.row !== frogPosition.row ||
        moveInfo.fromPosition.col !== frogPosition.col
      ) {
        return true;
      }

      // Count frog moves for debugging
      frogMovesCount++;
      this.addLog(
        `🐸 BLOCKING frog move: [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}]`
      );

      // BLOCK ALL frog moves - they should only be used by higher priorities
      // Higher priorities (1-15) will have already had their chance to use the frog
      // Lower priorities (17-19) should never waste the frog
      return false;
    });

    this.addLog(
      `🐸 Blocked ${frogMovesCount} frog moves out of ${allMoves.length} total moves`
    );
    return filteredMoves;
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }
}

